<script setup lang="ts">
import { computed, inject, reactive } from 'vue';
import { request } from '@/util/request';
import SelectRemote from '@/components/business/crud/common/SelectRemote.vue';
import SelectRemoteMulti from '@/components/business/crud/common/SelectRemoteMulti.vue';
import { WindowTool } from '@/components/common/window/WindowInterface';
import ProxyForm from '@/views/management/proxy/import/ProxyForm.vue';
import { message } from 'ant-design-vue';
import ScBtn from '@/components/common/widget/ScBtn.vue';
import { ProxyItem } from '@/views/management/proxy/import/ProxyTypes';

const props = defineProps<{
  onSubmit(items: any[]): Promise<boolean>;
  selectTagGroup: boolean;
}>();

const windowTool = inject<WindowTool | false>(WindowTool, false);

const data = reactive({
  stage: 0,
  data: {
    content: '',
    hasError: false,
    values: [] as ProxyItem[],
  },
  type: 'socks5',
  group: null as any,
  tags: [] as any,
  checking: false,
  removeIndex: {} as Record<string, true>,
  result: {} as Record<string, { outIp: string; area: string }>, // 检测结果
});

const resultTableData = computed(() => {
  return data.data.values
    .filter((i) => !data.removeIndex[i.index])
    .map((item) => {
      const result = getItemResult(item);
      return {
        index: item.index,
        raw: item.raw,
        outIp: result?.outIp || '',
        area: result?.area || '',
      };
    });
});

function getItemResult(item: ProxyItem) {
  const key = [
    data.type,
    item.host,
    item.port,
    item.username,
    item.password,
  ].join('|');
  return data.result[key];
}

function setItemResult(
  item: ProxyItem,
  result: { outIp: string; area: string }
) {
  const key = [
    data.type,
    item.host,
    item.port,
    item.username,
    item.password,
  ].join('|');
  data.result[key] = result;
}

// 连接检测
async function check() {
  if (data.data.hasError) {
    message.error('请先处理导入信息中的错误');
    return;
  }
  if (data.data.values.length === 0) {
    message.error('请先添加有效的导入信息');
    return;
  }
  data.stage = 1;
  await checkFail();
}

async function recheck(item: ProxyItem) {
  const res = await request('management/proxy/checkProxyInfo', {
    type: data.type,
    data: [item],
  });
  if (res) {
    setItemResult(item, res[0]);
  }
}

async function checkFail() {
  data.checking = true;
  const fails = data.data.values.filter((item) => !getItemResult(item));
  const res = await request('management/proxy/checkProxyInfo', {
    type: data.type,
    data: fails,
  });
  if (res) {
    fails.forEach((item, i) => {
      setItemResult(item, res[i]);
    });
  }
  data.checking = false;
}

async function removeFail() {
  data.data.values
    .filter((item) => !getItemResult(item))
    .map((item) => {
      data.removeIndex[item.index] = true;
    });
}

async function submit() {
  if (data.data.values.length === 0) {
    if (windowTool) {
      windowTool.close(true);
    }
    return;
  }
  const proxies = data.data.values.map((i) => {
    const checkResult = getItemResult(i);
    return {
      type: data.type,
      group: data.group?.id,
      tags: {
        attach: data.tags.map((i: any) => i.id),
      },
      host: i.host,
      port: i.port,
      username: i.username,
      password: i.password,
      expiredTime: i.expiredTime,
      status: checkResult ? 'normal' : 'error',
      outIp: checkResult?.outIp || '',
      area: checkResult?.area || '',
    };
  })
  const res = await props.onSubmit(proxies);
  if (res) {
    if (windowTool) {
      windowTool.close(true);
    }
  }
}

function toStage0() {
  data.removeIndex = {};
  data.stage = 0;
}

const groupWidget = {
  name: 'selectRemote',
  multi: false,
  path: 'management/proxy_group/index',
  // 标签字段名,默认name
  labelFieldName: 'name',
  // 数据字段名，默认id
  valueFieldName: 'id',
  // 创建页面路径
  createPagePath: 'management/proxy_group/create',
  // 查看页面路径，会替换__value__为字段值
  viewPagePath: 'management/proxy_group/view?id=__value__',
};
const tagWidget = {
  name: 'selectRemote',
  multi: true,
  path: 'management/proxy_tag/index',
  // 标签字段名,默认name
  labelFieldName: 'name',
  // 数据字段名，默认id
  valueFieldName: 'id',
  // 创建页面路径
  createPagePath: 'management/proxy_tag/create',
  // 查看页面路径，会替换__value__为字段值
  viewPagePath: 'management/proxy_tag/view?id=__value__',
};

const columns = [
  {
    title: '序号',
    dataIndex: 'index',
    key: 'index',
  },
  {
    title: '代理信息',
    dataIndex: 'raw',
    key: 'raw',
  },
  {
    title: '出口IP',
    dataIndex: 'outIp',
    key: 'outIp',
  },
  {
    title: '归属地',
    dataIndex: 'area',
    key: 'area',
  },
  {
    title: '检测结果',
    dataIndex: 'result',
    key: 'result',
  },
];
</script>

<template>
  <div class="form-box">
    <form class="form" v-if="data.stage === 0">
      <div class="form-main">
        <div class="form-body">
          <div class="form-group">
            <div class="label">代理类型</div>
            <a-select
              ref="select"
              v-model:value="data.type"
              style="width: 300px"
            >
              <a-select-option value="http">Http</a-select-option>
              <a-select-option value="socks5">Socks5</a-select-option>
            </a-select>
          </div>
          <div class="form-group">
            <div class="label">代理信息</div>
            <div class="desc">
              每行一条线路信息，支持以下格式：
              <br />
              格式1：ip:端口 账号 密码
              <br />
              格式2：ip|端口|账号|密码|到期日期
              <br />
              格式3：ip/端口/账号/密码
              <br />
              格式4：ip/端口/账号/密码/地区
            </div>
            <proxy-form v-model="data.data" />
          </div>
        </div>
      </div>
      <div class="form-btn-group">
        <sc-btn type="primary" :handler="check" text="连接检测" />
      </div>
    </form>
    <form class="form" v-if="data.stage === 1">
      <div class="form-main">
        <div class="form-body">
          <div class="form-group" v-show="selectTagGroup">
            <a-descriptions layout="vertical" bordered size="small">
              <a-descriptions-item label="分组">
                <select-remote
                  style="width: 100%"
                  title="分组"
                  :widget="groupWidget"
                  v-model="data.group"
                />
              </a-descriptions-item>
              <a-descriptions-item label="标签">
                <select-remote-multi
                  style="width: 100%"
                  title="标签"
                  :widget="tagWidget"
                  v-model="data.tags"
                />
              </a-descriptions-item>
            </a-descriptions>
          </div>
          <div style="margin-bottom: 10px">
            <sc-btn
              :handler="() => checkFail()"
              text="重新检测失败线路"
              style="margin-right: 8px"
            />
            <sc-btn text="移除失败线路" :handler="removeFail" />
          </div>
          <a-table
            :columns="columns"
            :data-source="resultTableData"
            :pagination="false"
            :loading="data.checking"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.dataIndex === 'result'">
                <template v-if="!data.checking">
                  <a-tag v-if="record.outIp" color="green"> 连接正常</a-tag>
                  <a-tag v-else color="red"> 连接失败</a-tag>
                </template>
              </template>
              <template v-else>
                {{ record[column.key] }}
              </template>
            </template>
          </a-table>
        </div>
      </div>
      <div class="form-btn-group">
        <sc-btn
          type="primary"
          :handler="submit"
          :text="data.data.values.length > 0 ? '导入' : '关闭'"
          style="margin-right: 20px"
        />
        <sc-btn :handler="toStage0" text="上一步" />
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.form-box {
  display: flex;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
}

.form {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  max-width: 1200px;

  .form-main {
    flex-grow: 1;
    overflow: auto;

    .form-body {
      padding: $spacing-extra-large;
      flex-grow: 1;
      overflow: auto;

      .form-group {
        padding-bottom: $spacing-medium;

        .label {
          padding-bottom: 12px;
        }

        .desc {
          color: $color-content-text-secondary;
          padding-bottom: 12px;
        }
      }
    }
  }

  .form-btn-group {
    border-top: 1px solid $color-content-border;
    padding: $spacing-medium;
  }
}
</style>
